import gql from "graphql-tag";
import { intervalToDuration } from "date-fns";
import { pick } from "lodash";

import { isDefined, isNullable } from "src/utils/types/types";
import { apolloCore } from "src/utils/apollo/apollo";
import {
  GenerateDataUploadUrlDocument,
  GenerateDataUploadUrlMutation,
  GenerateDataUploadUrlMutationVariables,
} from "src/gql/codegen/graphql";
import { Session } from "src/utils/users/types";
import type { ActiveContext } from "shared/ContextSwitcher/store";

export type ProgressData = {
  percentage: number;
  secondsRemaining: number | undefined;
  completed: boolean;
  abort: () => void;
};
type OnProgress = (progressData: ProgressData) => void;

const generateDataUploadUrl = async (
  session: Session,
  activeContext: ActiveContext,
  file: File
): Promise<string> => {
  const { data } = await apolloCore.mutate<
    GenerateDataUploadUrlMutation,
    GenerateDataUploadUrlMutationVariables
  >({
    mutation: GenerateDataUploadUrlDocument,
    variables: {
      context_owner: pick(activeContext, ["id", "type"]),
      content_length: file.size,
      file_name: file.name,
    },
    context: { session },
    fetchPolicy: "no-cache",
  });

  const url = data?.generate_data_upload_url?.url;

  if (isNullable(url)) {
    throw new Error("GenerateDataUploadUrl did not return a URL");
  }

  return url;
};

export class DataUploadAborted extends Error {}
const uploadFile = (presignedUrl: string, file: File, onProgress: OnProgress) =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const startedAt = new Date();
    xhr.upload.addEventListener("progress", (e) => {
      const { loaded, total } = e;
      const percentage = loaded / total;

      const secondsElapsed = intervalToDuration({
        start: startedAt,
        end: new Date(),
      }).seconds;
      let secondsRemaining: number | undefined;
      if (isDefined(secondsElapsed) && secondsElapsed !== 0) {
        const remainingBytes = total - loaded;
        const bytesPerSecond = loaded / secondsElapsed;
        secondsRemaining = remainingBytes / bytesPerSecond;
      }
      onProgress({
        percentage,
        secondsRemaining,
        completed: loaded === total,
        abort: xhr.abort.bind(xhr),
      });
    });
    xhr.addEventListener("load", () => {
      return resolve({ status: xhr.status, body: xhr.responseText });
    });
    xhr.addEventListener("error", () =>
      reject(new Error("File upload failed"))
    );
    xhr.addEventListener("abort", () =>
      reject(new DataUploadAborted("File upload aborted"))
    );
    xhr.open("PUT", presignedUrl, true);
    const formData = new FormData();
    formData.append(file.name, file);
    xhr.send(formData.get(file.name));
  });

export const uploadTable = async (
  session: Session,
  activeContext: ActiveContext,
  file: File,
  onProgress: OnProgress = () => {}
) => {
  onProgress({
    completed: false,
    percentage: 0,
    secondsRemaining: undefined,
    abort: () => {},
  });
  const presignedUrl = await generateDataUploadUrl(
    session,
    activeContext,
    file
  );
  await uploadFile(presignedUrl, file, onProgress);
};

gql`
  mutation GenerateDataUploadUrl(
    $context_owner: ContextOwner
    $content_length: Int!
    $file_name: String!
  ) {
    generate_data_upload_url(
      context_owner: $context_owner
      content_length: $content_length
      file_name: $file_name
    ) {
      url
    }
  }
`;
